// The Discord guide's walk-through of verifying, all in one panel: why it's needed, the four steps (with small mockups
// drawn in CSS, shown on request so the page stays calm), then where to get help.
// The wording matches the bot's real messages in the togebot repo, cogs/welcome/welcome.py.
import { useState } from 'react';
import { SectionHeading } from './GuideVisuals';

const CODE = '7KH3QM';
const USERNAME = 'yourname';
const STYLES = `
  @keyframes verifyPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
    50% { box-shadow: 0 0 0 3px rgba(255,255,255,0.35); }
  }
  .verify-next { animation: verifyPulse 1.8s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) { .verify-next { animation: none; } }
`;

const REASONS = [
  { title: 'It stops impersonation', text: 'Without a check, anyone could type a seller’s username and pose as them.' },
  { title: 'Your password is never needed', text: 'Just your username and a code that you add yourself.' },
  { title: 'Only your public bio is read', text: 'You can delete the code as soon as you’re verified.' },
];

const Tick = ({ color = '#23a55a' }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
);

const Pill = ({ color, next, children }) => (
  <span className={`inline-flex items-center justify-center gap-1 rounded font-semibold text-white whitespace-nowrap ${next ? 'verify-next' : ''}`} style={{ fontSize: '0.68rem', lineHeight: 1, padding: '6px 9px', background: color }}>{children}</span>
);

const Step = ({ number, title, text, show, children }) => (
  <li className="flex flex-col gap-2.5 min-w-0">
    <div className="flex items-start gap-2.5">
      <span className="flex items-center justify-center rounded-full flex-shrink-0 text-white font-bold" style={{ width: '24px', height: '24px', fontSize: '0.75rem', background: 'linear-gradient(135deg,#04aff7,#f42ae4)' }}>{number}</span>
      <div className="min-w-0">
        <div className="text-white font-semibold" style={{ fontSize: '0.85rem', lineHeight: 1.2 }}>{title}</div>
        <p className="text-zinc-400 mt-0.5" style={{ fontSize: '0.72rem', lineHeight: 1.35 }}>{text}</p>
      </div>
    </div>
    {show && <div className="rounded-xl p-2.5 flex-1 flex flex-col justify-center gap-2" style={{ background: '#1e1f22', minHeight: '132px' }}>{children}</div>}
  </li>
);

const OnlyYou = () => <p className="text-zinc-500" style={{ fontSize: '0.6rem', lineHeight: 1 }}>Only you can see this</p>;

export default function VerifyGuide({ verifyUrl }) {
  const [show, setShow] = useState(false);
  return (
    <section id="how-to-verify" className="flex flex-col gap-2.5" style={{ scrollMarginTop: '64px' }}>
      <style>{STYLES}</style>
      <SectionHeading>How to verify</SectionHeading>

      <div className="rounded-2xl shadow-[0_4px_16px_rgba(0,0,0,0.35)] p-3 flex flex-col gap-4" style={{ background: '#27272f' }}>
      <div className="rounded-xl p-3" style={{ background: 'rgba(35,165,90,0.1)', border: '1px solid rgba(35,165,90,0.4)' }}>
        <div className="text-white font-semibold" style={{ fontSize: '0.85rem', lineHeight: 1.2 }}>Why verify?</div>
        <p className="text-zinc-300 mt-1" style={{ fontSize: '0.72rem', lineHeight: 1.4 }}>It’s for every member’s protection and safety. Everyone here goes by their Whatnot username, so members need to know that name really is you. A short code in your bio proves it, because only you can edit your profile.</p>
        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2.5">
          {REASONS.map((reason) => (
            <li key={reason.title} className="flex items-start gap-1.5">
              <span style={{ marginTop: '2px' }}><Tick /></span>
              <span className="min-w-0">
                <span className="block text-white font-medium" style={{ fontSize: '0.72rem', lineHeight: 1.25 }}>{reason.title}</span>
                <span className="block text-zinc-400" style={{ fontSize: '0.68rem', lineHeight: 1.35 }}>{reason.text}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-4">
        <Step show={show} number={1} title="Enter your username" text="Tap Verify on the welcome message, then type your Whatnot username.">
          <div className="rounded-lg p-2.5" style={{ background: '#313338' }}>
            <div className="text-white font-semibold" style={{ fontSize: '0.78rem', lineHeight: 1.2 }}>Verify with Whatnot</div>
            <div className="uppercase font-bold text-zinc-300 mt-2" style={{ fontSize: '0.56rem', letterSpacing: '0.03em', lineHeight: 1 }}>Whatnot username</div>
            <div className="rounded mt-1 text-white" style={{ fontSize: '0.72rem', lineHeight: 1, padding: '7px 8px', background: '#1e1f22' }}>{USERNAME}</div>
            <div className="flex justify-end mt-2"><Pill color="#5865f2" next>Submit</Pill></div>
          </div>
        </Step>

        <Step show={show} number={2} title="Check it’s you" text="TOGΞ shows your Whatnot profile, so you can confirm it’s the right account.">
          <OnlyYou />
          <p className="text-white font-semibold" style={{ fontSize: '0.74rem', lineHeight: 1.2 }}>Is this you?</p>
          <img src="/guide/verify-card.webp" width={1200} height={620} alt="An example Whatnot profile card for @yourname, showing followers, following and when they joined Whatnot" loading="lazy" className="block w-full h-auto rounded-lg" />
          <div className="flex flex-wrap gap-1.5">
            <Pill color="#248046" next>✅ Yes, that’s me</Pill>
            <Pill color="#4e5058">Not me</Pill>
          </div>
        </Step>

        <Step show={show} number={3} title="Add the code to your bio" text="Paste your code anywhere in your Whatnot bio, save it, then tap Check my bio.">
          <div className="flex items-center justify-between gap-2 rounded" style={{ padding: '5px 8px', background: '#2b2d31', border: '1px solid #3f4147' }}>
            <span className="text-zinc-400" style={{ fontSize: '0.6rem', lineHeight: 1 }}>Your code</span>
            <code className="text-white font-bold" style={{ fontSize: '0.8rem', letterSpacing: '0.1em', lineHeight: 1 }}>{CODE}</code>
          </div>
          <div className="rounded-lg p-2" style={{ background: '#0f0f10', border: '1px solid rgba(251,229,16,0.45)' }}>
            <div className="flex items-center gap-1.5 text-zinc-400" style={{ fontSize: '0.58rem', lineHeight: 1 }}>
              <img src="/wnlogo.png" alt="" className="object-contain" style={{ width: '11px', height: '11px' }} />
              Your Whatnot bio
            </div>
            <p className="text-white mt-1.5" style={{ fontSize: '0.7rem', lineHeight: 1.3 }}>Card collector and breaker <span className="font-bold rounded" style={{ padding: '0 3px', background: '#fbe510', color: '#000000' }}>{CODE}</span></p>
          </div>
          <div className="flex justify-end"><Pill color="#248046" next>🔍 Check my bio</Pill></div>
        </Step>

        <Step show={show} number={4} title="You’re in!" text="Every channel unlocks straight away. You can take the code out of your bio now.">
          <OnlyYou />
          <p className="text-white font-semibold" style={{ fontSize: '0.74rem', lineHeight: 1.2 }}>🎉 You’re verified, @{USERNAME}!</p>
          <ul className="flex flex-col gap-1">
            {['Verified and Whatnot roles added', `Server name set to ${USERNAME}`, 'You can remove the code from your bio'].map((line) => (
              <li key={line} className="flex items-center gap-1.5 text-zinc-300" style={{ fontSize: '0.68rem', lineHeight: 1.2 }}><Tick />{line}</li>
            ))}
          </ul>
        </Step>
      </ol>

      <button onClick={() => setShow(!show)} aria-expanded={show}
        className="self-start flex items-center gap-1.5 rounded-full font-semibold transition-colors hover:bg-white/10"
        style={{ fontSize: '0.72rem', lineHeight: 1, padding: '7px 12px', background: 'rgba(255,255,255,0.06)', color: '#dbdee1' }}>
        {show ? 'Hide the examples' : '👀 Show me what each step looks like'}
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: show ? 'rotate(180deg)' : 'none' }} aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
      </button>

      <div className="flex flex-wrap items-center justify-between gap-2 pt-3" style={{ borderTop: '1px solid #3f3f46' }}>
        <p className="text-zinc-300 min-w-0" style={{ fontSize: '0.72rem', lineHeight: 1.35 }}>Not on Whatnot, or stuck on a step? Tap <span className="text-white font-semibold">Help</span> on the welcome message to open a private ticket.</p>
        <a href={verifyUrl} className="rounded font-semibold text-white flex-shrink-0 transition-transform active:scale-95" style={{ fontSize: '0.75rem', lineHeight: 1, padding: '8px 12px', background: '#248046' }}>Go to Verify</a>
      </div>
      </div>
    </section>
  );
}
